import type { Vec2 } from '../geo/types';
import type { CollisionWorld } from './types';

/** The water as a rowing boat meets it: a hull with a bearing, not a point. */
export interface BoatWater {
  /** Whether the whole boat can lie with its middle here on this bearing. */
  floats(position: Vec2, bearing: number): boolean;
  /** Where the boat's middle comes to after a step: the whole step, the part of it that slides
   *  along what the boat meets, or where it lay. */
  advance(position: Vec2, bearing: number, delta: Vec2): Vec2;
  /** The nearest water the middle of a hull can lie on. */
  nearest(p: Vec2): Vec2;
}

/** A boat is wide and low at the hull and narrow and tall at the people sitting in it, so it has
 *  two worlds: one of what stands at the height of the hull, one of what stands over it. Each is
 *  met by circles of its own width at stations along the middle of the boat. */
export interface BoatBody {
  readonly hull: CollisionWorld;
  readonly crew: CollisionWorld;
  readonly hullStations: readonly number[];
  readonly crewStations: readonly number[];
}

/** The way a bearing points on the layout. A bearing is a camera yaw: zero looks north. */
export function ahead(bearing: number): Vec2 {
  return [-Math.sin(bearing), Math.cos(bearing)];
}

export function createBoatWater({ hull, crew, hullStations, crewStations }: BoatBody): BoatWater {
  const parts = [
    ...hullStations.map((station) => ({ world: hull, station })),
    ...crewStations.map((station) => ({ world: crew, station })),
  ];
  const at = (position: Vec2, bearing: number, station: number): Vec2 => {
    const [x, y] = ahead(bearing);
    return [position[0] + x * station, position[1] + y * station];
  };

  function floats(position: Vec2, bearing: number): boolean {
    return parts.every(({ world, station }) => world.contains(at(position, bearing, station)));
  }

  return {
    floats,
    advance(position, bearing, delta) {
      // The part that meets something first sets the step for the whole boat.
      const step = parts.reduce<Vec2>((least, { world, station }) => {
        const from = at(position, bearing, station);
        const to = world.move(from, delta);
        const moved: Vec2 = [to[0] - from[0], to[1] - from[1]];
        return Math.hypot(moved[0], moved[1]) < Math.hypot(least[0], least[1]) ? moved : least;
      }, delta);
      const next: Vec2 = [position[0] + step[0], position[1] + step[1]];
      return floats(next, bearing) ? next : position;
    },
    nearest: (p) => hull.nearestWalkable(p),
  };
}

/** A boat water that is built the first time a boat is put on it, not while the city loads. */
export function deferBoatWater(build: () => BoatWater): BoatWater {
  let built: BoatWater | null = null;
  const water = (): BoatWater => (built ??= build());
  return {
    floats: (position, bearing) => water().floats(position, bearing),
    advance: (position, bearing, delta) => water().advance(position, bearing, delta),
    nearest: (p) => water().nearest(p),
  };
}
